import React from 'react';
import { Helmet } from 'react-helmet-async';

type HeadBlockProps = {
  title?: string;
  ogTitle?: string;
  description?: string;
  ogDescription?: string;
  img?: string;
  canonical?: string;
  firstScript?: string;
  secondScript?: string;
  noindex?: boolean;
};

export const HeadBlock = ({
  title = initialValue.title,
  ogTitle = initialValue.ogTitle,
  description = initialValue.description,
  ogDescription = initialValue.ogDescription,
  img = initialValue.img,
  canonical = initialValue.canonical,
  firstScript = initialValue.firstScript,
  secondScript = initialValue.secondScript,
  noindex = initialValue.noindex,
}: HeadBlockProps): React.ReactElement => {
  return (
    <Helmet>
      <title>{title}</title>
      <meta property="og:title" content={ogTitle} />
      <meta property="og:description" content={ogDescription} />
      <meta name="description" content={description}></meta>
      <meta property="og:image" content={img} />
      <link rel="canonical" href={canonical} />
      <script type="application/ld+json">{firstScript}</script>
      <script type="application/ld+json">{secondScript}</script>
      {noindex && <meta name="robots" content="noindex" />}
    </Helmet>
  );
};

const initialValue = {
  title:
    'みんかぶアセットプランナー｜投資管理アプリ 株式投資の資産推移をグラフ化',
  ogTitle:
    'みんかぶアセットプランナー｜投資管理アプリ 株式投資の資産推移をグラフ化',
  description:
    'みんかぶアセットプランナーは、株式・投資信託を管理、分析する投資管理アプリです。株式の取引履歴や資産推移をグラフ化し、損益をLINEで毎日お知らせ。個人投資家の資産形成をサポートします。',
  ogDescription:
    'みんかぶアセットプランナーは、株式・投資信託を管理、分析する投資管理アプリです。株式の取引履歴や資産推移をグラフ化し、損益をLINEで毎日お知らせ。個人投資家の資産形成をサポートします。',
  img: 'https://tw.minkabu.jp/static/images/lp/walk-img-4.png',
  canonical: 'https://tw.minkabu.jp/',
  firstScript: `{
    "@context": "https://schema.org/",
    "@type": "WebPage",
    "name": "アセプラ(みんかぶアセットプランナー)",
    "image": [
      "https://tw.minkabu.jp/static/images/lp/mainimage.jpg",
      "https://tw.minkabu.jp/static/images/lp/home_shisan.png",
      "https://tw.minkabu.jp/static/images/lp/aruaru_rikaku.png",
      "https://tw.minkabu.jp/static/images/lp/oshirase.png",
      "https://tw.minkabu.jp/mg/lp/aruaru_songiri.png"
     ],
    "description": "みんかぶアセットプランナーは、株式・投資信託を管理、分析する投資管理アプリです。株式の取引履歴や資産推移をグラフ化し、損益をLINEで毎日お知らせ。個人投資家の資産形成をサポートします。",
    "brand": {
      "@type": "Brand",
      "name": "みんかぶ"
    }
  }`,
  secondScript: `{
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "みんかぶアセットプランナー（アセプラ）とは何ですか？",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "みんかぶアセットプランナーとは、株式や投資信託などを管理、分析する投資管理アプリです。証券口座や銀行口座にある資産を一元管理し、投資を分析できます。"
        }
      },
      {
        "@type": "Question",
        "name": "みんかぶアセットプランナー（アセプラ）でどんなことができますか？",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "株式、投資信託などの資産や損益をまとめて管理できます。取引履歴の分析や配当金を管理できるほか、老後資金など将来必要なお金の貯め方、増やし方の提案を受けることができます。配当金管理について詳しくは「配当金を管理したい」にお答えしますをご参照ください。"
        }
      },
      {
        "@type": "Question",
        "name": "連携できる証券会社はどこですか？",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "SBI証券、楽天証券をご連携いただけます。その他の証券会社は連携準備中となります（2025年8月時点）。<br>連携準備中の証券会社はMoneytreeと連携することで一部の資産を管理できます。"
        }
      },
      {
        "@type": "Question",
        "name": "連携できる金融商品はなんですか？",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "国内株式（現物、信用）、外国株式（現物）、投資信託、ETF・ETN（国内、外国）、REIT（国内、外国）、債券（国内、外国）、EB（国内、外国）、ロボアドバイザー、現金を連携できます。不動産、ゴールドなどは、手入力で管理できます。"
        }
      },
      {
        "@type": "Question",
        "name": "銀行口座も連携できますか？",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "みんかぶアセットプランナーでは家計簿アプリ「マネーツリー」と連携し、銀行口座の「残高」や「取引/入出金履歴」を管理することができます。※ Moneytree IDと連携する必要があります。詳しくは「銀行口座を連携したい」にお答えしますをご参照下さい。"
        }
      },
      {
        "@type": "Question",
        "name": "おすすめの国内株式を提案する機能はありますか？",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "みんかぶアセットプランナーには国内株式を提案する機能はありません。みんかぶの「株式ランキング」や「銘柄スクリーニング」などで銘柄を探すことができます。"
        }
      },
      {
        "@type": "Question",
        "name": "みんかぶアセットプランナーの利用料金を教えてください",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "全ての機能を利用するには「みんかぶプレミアム（税込1,320円/月）」にご登録が必要です。<br>初回登録のみ30日間無料でご利用いただけます。"
        }
      }
    ]
  }`,
  noindex: false,
};
